import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import UmamiContext from "@/contexts/UmamiContext";
import { setLanguage } from "@/features/auth/api";
import { useUser } from "@/features/auth/hooks/useUser";
import { errorToast } from "@/ui/toast/Toast";
import i18n, { applyLanguage, beginSave, endSave, getLanguage } from "@/i18n";
import {
	DEFAULT_LANGUAGE,
	isEnglishEnabled,
	isSupportedLanguage,
	withAccountLanguage,
} from "@/i18n/language";
import { createLanguageSaver } from "@/i18n/languageSaver";

// The header, footer and settings switchers all call changeLanguage, so they
// share one saver: one queue for the page, not one per mounted hook. The app
// has a single QueryClient; changeLanguage records it here so the saver reads
// the whoami cache as it is when a save settles, not as it was when it began.
let activeQueryClient = null;

const languageSaver = createLanguageSaver({
	save: setLanguage,
	// Only the account that switched is stamped: someone else may have signed in
	// while the request was in flight.
	onSaved: (language, { email }) => {
		activeQueryClient?.setQueryData(["whoami"], (data) =>
			withAccountLanguage(data, email, language)
		);
	},
	apply: applyLanguage,
	// What the account is confirmed to have: whoami, as every successful save
	// has updated it.
	getAccountLanguage: () => activeQueryClient?.getQueryData(["whoami"])?.user?.language,
	onFailed: () => errorToast(i18n.t("common:language.saveFailed")),
});

// Switching applies at once. Signed in, it is then saved to the account, one
// save at a time in click order; if the latest one fails the device goes back
// to the language the account has, so it never disagrees with the account every
// other device follows.
export function useLanguage() {
	const { i18n: instance } = useTranslation();
	const { isLoggedIn, user } = useUser();
	const queryClient = useQueryClient();
	const { trackEvent } = useContext(UmamiContext);
	const enabled = isEnglishEnabled(import.meta.env);
	const email = user?.email;

	const changeLanguage = useCallback(
		async (next) => {
			if (!enabled || !isSupportedLanguage(next)) return;

			const previous = getLanguage();
			if (next === previous) return;

			applyLanguage(next);
			// Analytics is a side note: a tracker that throws must never skip the
			// save below, or the account would silently keep the old language.
			try {
				trackEvent("language-change", { from: previous, to: next });
			} catch {
				// Not worth surfacing to the user.
			}

			if (!isLoggedIn) return;

			activeQueryClient = queryClient;
			beginSave();
			try {
				await languageSaver(next, { email });
			} finally {
				endSave();
			}
		},
		[enabled, isLoggedIn, email, queryClient, trackEvent]
	);

	return {
		language: instance.resolvedLanguage ?? DEFAULT_LANGUAGE,
		changeLanguage,
		enabled,
	};
}
